import type { ClientOverview, Currency } from '../../types/api';
import { convertUSDToCurrency, convertUSDToCurrencyFormatted } from './currencyConversion';

/**
 * Calculate total assets USD for a client across all units
 * @param clientOverviewData - Array of ClientOverview data for all units
 * @returns Total assets USD value
 */
export function calculateTotalAssetsUSD(clientOverviewData: ClientOverview[]): number {
  if (!clientOverviewData || clientOverviewData.length === 0) {
    return 0;
  }

  return clientOverviewData.reduce((total, overview) => {
    return total + (overview.total_assets_usd || 0);
  }, 0);
}

/**
 * Calculate total assets USD with formatting
 * @param clientOverviewData - Array of ClientOverview data for all units
 * @returns Formatted total assets USD string
 */
export function calculateTotalAssetsUSDFormatted(clientOverviewData: ClientOverview[]): string {
  const total = calculateTotalAssetsUSD(clientOverviewData);
  
  if (total === 0) {
    return '0';
  }
  
  // Format with commas for thousands
  return total.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
}

/**
 * Calculate total assets in a specific currency
 * @param clientOverviewData - Array of ClientOverview data for all units
 * @param targetCurrencyCode - Target currency code
 * @param currencies - Array of currency data
 * @returns Total assets value in target currency
 */
export function calculateTotalAssetsInCurrency(
  clientOverviewData: ClientOverview[],
  targetCurrencyCode: string,
  currencies: Currency[]
): number {
  const totalUSD = calculateTotalAssetsUSD(clientOverviewData);
  return convertUSDToCurrency(totalUSD, targetCurrencyCode, currencies);
}

/**
 * Calculate total assets in a specific currency with formatting
 * @param clientOverviewData - Array of ClientOverview data for all units
 * @param targetCurrencyCode - Target currency code
 * @param currencies - Array of currency data
 * @returns Formatted total assets string in target currency
 */
export function calculateTotalAssetsInCurrencyFormatted(
  clientOverviewData: ClientOverview[],
  targetCurrencyCode: string,
  currencies: Currency[]
): string {
  const totalUSD = calculateTotalAssetsUSD(clientOverviewData);
  return convertUSDToCurrencyFormatted(totalUSD, targetCurrencyCode, currencies, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useCurrencySymbol: false
  });
}












